import { useState, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { FavoriteItem, FavoriteType } from '@/types/favorites';
import { addFavoriteAction, deleteFavoriteAction, getFavoritesAction } from '@/utils/favorites.actions';
import { useIsUserAuthorized } from "@/utils/authHelpers";

export const useFavorites = () => {
  const { data: session } = useSession();
  const userAccessToken = session?.accessToken;
  const isAuthorized = useIsUserAuthorized();

  const [favoriteItems, setFavoriteItems] = useState<FavoriteItem[]>([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);

  // helper for token check
  const handleAddToFavorites = useCallback(async (favoriteId: string, favoriteType: FavoriteType) => {
    if (!isAuthorized) {
      throw new Error("User not authorized");
    }

    try {
      const res = await addFavoriteAction(userAccessToken!, favoriteId, favoriteType);
      if (!res) {
        console.error('Failed to add favorite');
        return;
      }
      setFavoriteItems(prev => [...prev, { type: favoriteType, id: favoriteId }]);
      console.log('Favorite added successfully: ', favoriteId);
    } catch (err) {
      console.error('Error adding favorite:', err);
      throw err;
    }
  }, [userAccessToken]);

  const handleGetFavorites = useCallback(async (favoritesType?: FavoriteType) => {
    if (!isAuthorized) {
      throw new Error("User not authorized");
    }

    try {
      const res = await getFavoritesAction(userAccessToken!, favoritesType);
      setFavoriteItems(res || []);
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setFavoriteItems([]);
      throw err;
    }
  }, [userAccessToken]);

  const handleDeleteFromFavorites = useCallback(async (favoriteId: string, favoriteType: FavoriteType) => {
    if (!isAuthorized) {
      throw new Error("User not authorized");
    }

    if (!favoriteId || !favoriteType) {
      console.error('Invalid favorite data');
      return;
    }

    try {
      const res = await deleteFavoriteAction(userAccessToken!, favoriteId, favoriteType);
      if (!res) {
        console.error('Failed to delete favorite');
        return;
      }
      setFavoriteItems(prev => prev.filter(item => item.id !== favoriteId || item.type !== favoriteType));
      console.log('Favorite deleted successfully: ', favoriteId);
    } catch (err) {
      console.error('Error deleting favorite:', err);
      throw err;
    }
  }, [userAccessToken]);

  const toggleSingleFavorite = useCallback(async (favoriteId: string, favoriteType: FavoriteType) => {
    const exists = favoriteItems.some(item => item.id === favoriteId && item.type === favoriteType);
    try {
      if (exists) {
        await handleDeleteFromFavorites(favoriteId, favoriteType);
      } else {
        await handleAddToFavorites(favoriteId, favoriteType);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      throw err;
    }
  }, [favoriteItems, handleAddToFavorites, handleDeleteFromFavorites]);

  const loadInitialFavorites = useCallback(async () => {
    try {
      await handleGetFavorites(FavoriteType.SAMPLE); // temporary, when favorite albums will appear need to delete parameter and modify logic a little
    } catch (err) {
      console.error('Error loading initial favorites:', err);
    } finally {
      setFavoritesLoaded(true);
    }
  }, [handleGetFavorites]);

  const isFavorite = useCallback((favoriteId: string) => {
    return favoriteItems.some(item => item.id === favoriteId);
  }, [favoriteItems]);

  return {
    favoriteItems,
    favoritesLoaded,
    handleAddToFavorites,
    handleGetFavorites,
    handleDeleteFromFavorites,
    toggleSingleFavorite,
    loadInitialFavorites,
    isFavorite
  };
};
